import { getLogger } from "@marble/logger";
import type { Prisma } from "@prisma/client";
import { PrismaClient } from "@prisma/client";
import { createEmailNormalizationExtension } from "./email-normalization";
import { createQueryInstrumentationExtension } from "./query-instrumentation";
import { getDatabaseUrl } from "./utils";

const logger = getLogger("db:client");

export const createPrismaClient = async (opts?: Prisma.PrismaClientOptions) => {
  const databaseUrl = opts?.datasourceUrl ?? (await getDatabaseUrl());

  if (databaseUrl.includes("production")) {
    globalThis.IS_PRODUCTION_DB = true;
  }

  const clientOpts: Prisma.PrismaClientOptions = { ...opts };
  clientOpts.datasourceUrl ||= databaseUrl;

  // Every runtime — the Next.js app, both standalone services, every Lambda and
  // every script — gets its Prisma client from here, so instrumenting this one
  // construction site instruments the whole fleet.
  //
  // The extensions are transparent: they add no models, fields or methods, so
  // we cast back to `PrismaClient` to keep the exported type (and therefore
  // every consumer's types) unchanged.
  const client = new PrismaClient(clientOpts)
    .$extends(createEmailNormalizationExtension())
    .$extends(createQueryInstrumentationExtension()) as unknown as PrismaClient;

  logger.info("Loaded prisma client");

  return client;
};

declare const globalThis: {
  prismaDbGlobal: PrismaClient;
  IS_PRODUCTION_DB: boolean;
} & typeof global;

let prisma = {} as PrismaClient;

if (typeof window === "undefined") {
  try {
    prisma = globalThis.prismaDbGlobal ?? (await createPrismaClient());
    if (process.env.NODE_ENV !== "production") {
      globalThis.prismaDbGlobal = prisma;
    }
  } catch (err) {
    logger.warn(err, "Error loading prisma client");
  }
}

export { prisma };
